import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, ArrowLeft, Loader2, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NewsDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPost();
    }, [slug]);

    const fetchPost = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/news/slug/${slug}`);
            setPost(response.data);
            
            // Set Page Title
            document.title = `${response.data.title} - Spencer Green Hotel`;
        } catch (error) {
            console.error('Error fetching post:', error);
            toast.error('Berita tidak ditemukan');
            navigate('/news');
        } finally {
            setLoading(false);
        }
    };

    const handleShare = async () => {
        const shareData = {
            title: post?.title,
            text: post?.excerpt,
            url: window.location.href,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(window.location.href);
                toast.success('Link disalin ke clipboard');
            }
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    if (loading) {
        return (
            <div className="bg-emerald-50/30 min-h-screen flex justify-center items-center">
                <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
            </div>
        );
    }

    if (!post) return null;

    return (
        <div className="bg-emerald-50/30 min-h-screen pb-24">
            {/* Header Image */}
            <div className="relative w-full h-[40vh] sm:h-[50vh] lg:h-[60vh] bg-emerald-950">
                {post.image_url ? (
                    <img 
                        src={post.image_url.replace('/upload/', '/upload/q_auto,f_auto/')} 
                        alt={post.title} 
                        className="w-full h-full object-cover opacity-60"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center opacity-20">
                        <span className="font-display text-white text-6xl">News</span>
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-950/40 to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-12 max-w-4xl mx-auto">
                    <Link to="/news" className="inline-flex items-center text-emerald-300 hover:text-white transition-colors mb-6 font-medium">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Kembali ke Daftar Berita
                    </Link>
                    <div className="flex items-center gap-2 text-sm text-emerald-200 mb-4">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(post.created_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-medium text-white leading-tight mb-4">
                        {post.title}
                    </h1>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
                <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-10 lg:p-12">
                    <div className="flex justify-between items-center pb-8 mb-8 border-b border-gray-100">
                        <p className="text-lg text-gray-500 italic">
                            {post.excerpt}
                        </p>
                        <button 
                            onClick={handleShare}
                            className="p-3 bg-gray-50 text-gray-600 rounded-full hover:bg-emerald-50 hover:text-emerald-600 transition-colors flex-shrink-0 ml-4"
                            title="Bagikan"
                        >
                            <Share2 className="w-5 h-5" />
                        </button>
                    </div>

                    <div 
                        className="prose prose-lg prose-emerald max-w-none text-gray-700 w-full whitespace-pre-wrap
                                  prose-headings:font-display prose-headings:font-medium prose-headings:text-gray-900
                                  prose-a:text-emerald-600 hover:prose-a:text-emerald-700 prose-a:break-all
                                  prose-img:rounded-xl prose-img:shadow-md"
                        dangerouslySetInnerHTML={{ __html: post.content ? post.content.replace(/<p><br><\/p>/g, '<div class="h-6 w-full"></div>').replace(/<p>\s*<\/p>/g, '<div class="h-6 w-full"></div>').replace(/<p>&nbsp;<\/p>/g, '<div class="h-6 w-full"></div>').replace(/&nbsp;/g, ' ') : '' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default NewsDetail;
